import { GridLegacy as Grid, Stack, styled, Tooltip, useTheme } from '@mui/material'
import { OptionsMenu, tokens, Typography } from 'ComponentLibrary'
import LoadingIndicator from '../../atoms/LoadingIndicator/LoadingIndicator'
import Colors from '../../styles/colors'
import { MenuOption } from '../../atoms/Options/OptionsMenu'
import {
    contentToHeadlineSpacing,
    headlineToContentSpacing,
    headlineToHeadlineSpacing,
    HeadlineVariants,
} from 'ComponentLibrary/src/styles/moduleSpacings'
import { ReactNode } from 'react'
import { Icon as IconType, LockSimple, LockSimpleOpen, PlusCircleIcon } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButton'

export type HeadlineWithSpacingProps = {
    title: string
    subtitle?: string
    isPersisting?: boolean
    className?: string
    options?: MenuOption[]
    variant?: HeadlineVariants
    locked?: boolean
    setLocked?: (isLocked: boolean) => void
    onClickAddItem?: () => void
    isAddDisabled?: boolean
    actions?: Action[]
    optionsIcon?: 'dots' | 'plus'
    endAdornment?: ReactNode
    endAdornmentStyle?: React.CSSProperties
    xs?: number
    details?: string
}

type Action = {
    icon: IconType
    callback: () => void
    disabled?: boolean
    tooltip?: string
    ariaLabel?: string
}

const VARIANT_CONFIGURATION = {
    headline1: {
        typography: 'headline1',
        color: Colors.customtext.darkblue100,
    },
    headline2: {
        typography: 'headline2',
        color: Colors.customtext.darkblue100,
    },
    headline3: {
        typography: 'headline3',
        color: Colors.customtext.darkblue100,
    },
    headline4: {
        typography: 'headline4',
        color: Colors.customtext.darkblue100,
    },
    headline5: {
        typography: 'headline5',
        color: Colors.customtext.darkblue100,
    },
} as const

export default function HeadlineWithSpacing({
    isPersisting,
    title,
    subtitle,
    options,
    className,
    onClickAddItem,
    isAddDisabled,
    locked,
    setLocked,
    variant: size = 'headline2',
    optionsIcon = 'dots',
    endAdornment,
    actions,
    xs = 12,
    details,
    endAdornmentStyle,
}: //this the icon for opening a menu. Though it can be a plus icon, it is different from the add item plus icon
HeadlineWithSpacingProps) {
    const theme = useTheme()

    const CustomIconButton = ({ callback, icon: Icon, disabled, tooltip, ariaLabel }: Action) => {
        const CustomAction = () => (
            <IconButton
                icon={<Icon />}
                size={size === 'headline1' ? 'medium' : 'small'}
                variant="tertiary"
                disabled={disabled}
                onClick={callback}
                ariaLabel={ariaLabel ?? `${title} Optionen`}
            />
        )

        if (tooltip) {
            return (
                <Tooltip title={tooltip}>
                    <span>
                        <CustomAction />
                    </span>
                </Tooltip>
            )
        }

        return <CustomAction />
    }
    return (
        <HeadlineContainer item variant={size} className="headline" xs={xs}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" className={className}>
                <div>
                    <Typography
                        variant={VARIANT_CONFIGURATION[size].typography}
                        color={isPersisting ? Colors.secondary1.contrast : tokens.ColorTextBoldest}
                    >
                        {title}
                    </Typography>
                    {details && (
                        <Typography style={{ paddingTop: theme.spacing(1) }} variant="bodyTextSmall" color={Colors.customtext.black60}>
                            {details}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography
                            style={{ paddingTop: theme.spacing(2) }}
                            variant="bodyTextLarge"
                            color={isPersisting ? Colors.secondary1.contrast : Colors.customtext.darkblue100}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </div>
                <EndAdornment sx={endAdornmentStyle}>
                    {isPersisting && <LoadingIndicator title="speichern..." isPending={isPersisting} height={1} />}
                    {endAdornment}
                    {onClickAddItem && !isPersisting && (
                        <Tooltip title={`${title} hinzufügen`}>
                            <IconButton
                                icon={<PlusCircleIcon />}
                                size={size === 'headline1' ? 'medium' : 'small'}
                                variant="tertiary"
                                onClick={onClickAddItem}
                                ariaLabel={`${title} hinzufügen`}
                                disabled={isAddDisabled}
                            />
                        </Tooltip>
                    )}
                    {options && !isPersisting && (
                        <OptionsMenu
                            options={options}
                            size={size === 'headline1' ? 'medium' : 'small'}
                            ariaLabel={`${title} Optionen`}
                            tooltip={'Optionen'}
                            icon={optionsIcon}
                        />
                    )}
                    {actions?.map((click, index) => {
                        return (
                            <CustomIconButton
                                icon={click.icon}
                                key={index}
                                ariaLabel={click.ariaLabel}
                                disabled={click.disabled}
                                callback={click.callback}
                                tooltip={click.tooltip}
                            />
                        )
                    })}
                    {locked !== undefined && (
                        <IconButton
                            icon={locked ? <LockSimple /> : <LockSimpleOpen />}
                            size={'small'}
                            disabled={setLocked === undefined}
                            onClick={() => {
                                setLocked?.(!locked)
                            }}
                            ariaLabel={`${title} ${locked ? 'entsperren' : 'sperren'}`}
                        />
                    )}
                </EndAdornment>
            </Stack>
        </HeadlineContainer>
    )
}

const EndAdornment = styled('div')`
    margin-left: auto;
    display: flex;
    align-items: center;
`

const HeadlineContainer = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: HeadlineVariants }>(({ theme, variant }) => ({
    //cases where headline follows a content block (default padding)
    '&.headline': {
        marginTop: `${contentToHeadlineSpacing(variant)}px`,
        paddingTop: 0,
        //TODO: REMOVE THIS LINE once firefox supports :has()
        marginBottom: `${headlineToContentSpacing(variant)}px`,
    },
    '& + .MuiGrid-item': {
        paddingTop: 0,
    },

    //cases where a headline follows another headline
    '.headline + &': { marginTop: `${headlineToHeadlineSpacing(variant, false)}px` },
    '.headline + * > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant, false)}px` },
    '.headline + * > *:first-child > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant, false)}px` },
    '.headline + * > *:first-child > *:first-child > &:first-child': {
        marginTop: `${headlineToHeadlineSpacing(variant, false)}px`,
    },
    /*
    TOOD: RE-ENABLE once firefox supports :has()
    '*:has(> .headline:last-child) + &': { marginTop: `${headlineToHeadlineSpacing(variant)}px` },
    '*:has(> .headline:last-child) + * > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant)}px` },
    '*:has(> .headline:last-child) + * > *:first-child > &:first-child': {
        marginTop: `${headlineToHeadlineSpacing(variant)}px`,
    },
    '*:has(> .headline:last-child) + * > *:first-child > *:first-child > &:first-child': {
        marginTop: `${headlineToHeadlineSpacing(variant)}px`,
    },
    */

    //cases where a headline is at the top of the page
    'main > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant, true)}px` },
    'main > *:first-child > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant, true)}px` },
    'main > *:first-child > *:first-child > &:first-child': { marginTop: `${headlineToHeadlineSpacing(variant, true)}px` },
    'main > *:first-child > *:first-child > *:first-child > &:first-child': {
        marginTop: `${headlineToHeadlineSpacing(variant, true)}px`,
    },
    [theme.breakpoints.up('singleColumn')]: {
        //special case when there is a headline in the same row as another headline, and both are at the top of the page
        'main > *:first-child > .pageRow:first-child > *:last-child > &.headline:first-child': {
            marginTop: `${headlineToHeadlineSpacing(variant, true)}px`,
        },
    },

    //cases where content block follows a headline
    /*
    TOOD: RE-ENABLE once firefox supports :has()
    '& + *:not(.headline):not(:has(> .headline:first-child)):not(:has(> *:first-child >.headline:first-child))': {
        marginTop: `${headlineToContentSpacing(variant)}px`,
        '&.MuiGrid-item': { paddingTop: 0 },
    },
    '*:has(> &:last-child) + *:not(.headline):not(:has(> .headline:first-child)):not(:has(> *:first-child >.headline:first-child))': {
        marginTop: `${headlineToContentSpacing(variant)}px`,
        '&.MuiGrid-item': { paddingTop: 0 },
    },
    */
    //Very soon, FireFox will also support :has selector
}))
